import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  fetchSignInMethodsForEmail,
  applyActionCode,
  checkActionCode,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  deleteDoc,
  onSnapshot,
  addDoc,
  getCountFromServer,
  Timestamp,
  limit,
  enableNetwork,
  disableNetwork,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { logDebug, logWarn, logError, logInfo } from "@/utils/secureLogger";

// Your Firebase configuration
// Values are loaded from environment variables for security
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
// Initialize Functions with the us-central1 region
const functions = getFunctions(app, "us-central1");

// Set VITE_USE_FIREBASE_EMULATOR=true in .env.local to use emulator
const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true";
if (useEmulator) {
  const emulatorHost = import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST || "localhost";
  const emulatorPort = parseInt(import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || "5001");
  connectFunctionsEmulator(functions, emulatorHost, emulatorPort);
  console.log(`🔧 Connected to Functions Emulator at ${emulatorHost}:${emulatorPort}`);
}
// Production Cloud Functions will be used automatically when not using emulator

// Network connectivity and error monitoring
let isMonitoringNetwork = false;
let reconnectionAttempts = 0;
const MAX_RECONNECTION_ATTEMPTS = 3;

/**
 * Monitor network connectivity and handle Firestore connection issues
 * This helps recover from QUIC protocol errors and network disruptions
 */
function setupFirestoreNetworkMonitoring() {
  if (isMonitoringNetwork) return;
  isMonitoringNetwork = true;

  // Monitor network status changes
  window.addEventListener('online', async () => {
    logInfo('🌐 Network connection restored, re-enabling Firestore');
    try {
      await enableNetwork(db);
      reconnectionAttempts = 0;
      logInfo('✅ Firestore network re-enabled successfully');
    } catch (error) {
      logError('❌ Failed to re-enable Firestore network', error);
    }
  });

  window.addEventListener('offline', () => {
    logWarn('🔌 Network connection lost, Firestore operating in offline mode');
  });

  // Monitor visibility changes to detect tab switches and reconnect if needed
  let wasHidden = false;
  document.addEventListener('visibilitychange', async () => {
    if (document.hidden) {
      wasHidden = true;
    } else if (wasHidden && navigator.onLine) {
      // Tab became visible after being hidden, verify Firestore connection
      wasHidden = false;
      logDebug('👁️ Tab became visible, verifying Firestore connection');
      
      // Give Firestore a moment to auto-reconnect
      setTimeout(async () => {
        if (reconnectionAttempts < MAX_RECONNECTION_ATTEMPTS) {
          try {
            // Attempt to refresh the connection by toggling network
            await disableNetwork(db);
            await new Promise(resolve => setTimeout(resolve, 100));
            await enableNetwork(db);
            reconnectionAttempts = 0;
            logDebug('✅ Firestore connection refreshed after tab visibility change');
          } catch (error) {
            reconnectionAttempts++;
            logWarn(`⚠️ Firestore reconnection attempt ${reconnectionAttempts} failed`, error);
          }
        }
      }, 1000);
    }
  });

  logInfo('🔍 Firestore network monitoring enabled');
}

// Initialize network monitoring
if (typeof window !== 'undefined') {
  setupFirestoreNetworkMonitoring();
}

export {
  auth,
  db,
  storage,
  functions,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  fetchSignInMethodsForEmail,
  applyActionCode,
  checkActionCode,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  deleteDoc,
  onSnapshot,
  addDoc,
  getCountFromServer,
  Timestamp,
  limit,
  storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  httpsCallable,
  enableNetwork,
  disableNetwork,
};
